import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './SocioDashboard.css';
import MisDeudas from '../components/MisDeudas';
import {
  getNombreMes,
  calcularCuotaMensual,
  generarCronogramaAmortizacion,
  obtenerPeriodoDeHoy,
  esPrestamoActivo
} from '../utils/amortizacion';

const SocioDashboard = () => {
  const { user, isSocio } = useAuth();
  const [loading, setLoading] = useState(true);
  const [socioData, setSocioData] = useState(null);
  const [prestamos, setPrestamos] = useState([]);
  const [loadingPrestamos, setLoadingPrestamos] = useState(true);
  const [registrosMensuales, setRegistrosMensuales] = useState([]);
  const [loadingRegistros, setLoadingRegistros] = useState(true);
  const [prestamosCompletados, setPrestamosCompletados] = useState({});
  const [mesReunion, setMesReunion] = useState(new Date().getMonth() + 1);
  const [añoReunion, setAñoReunion] = useState(new Date().getFullYear());
  const [organizadoresReunion, setOrganizadoresReunion] = useState(null);
  const [loadingOrganizadores, setLoadingOrganizadores] = useState(false);

  const fetchOrganizadoresReunion = useCallback(async (mes, año) => {
    try {
      setLoadingOrganizadores(true);
      const response = await axios.get(`/api/users/reunion/mes/${mes}/año/${año}`);
      setOrganizadoresReunion(response.data);
    } catch (error) {
      console.error('Error fetching organizadores:', error);
      setOrganizadoresReunion(null);
    } finally {
      setLoadingOrganizadores(false);
    }
  }, []);

  const fetchSocioData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/users/${user.id}`);
      setSocioData(response.data.user);
    } catch (error) {
      console.error('Error fetching socio data:', error);
      toast.error('Error al cargar información del socio');
    }
  }, [user.id]);

  const fetchPrestamos = useCallback(async () => {
    try {
      const response = await axios.get('/api/solicitudes-prestamo/mis-solicitudes');
      setPrestamos(response.data.data);
    } catch (error) {
      console.error('Error fetching prestamos:', error);
      toast.error('Error al cargar las solicitudes de préstamo');
    } finally {
      setLoadingPrestamos(false);
    }
  }, []);

  const fetchRegistrosMensuales = useCallback(async () => {
    try {
      const response = await axios.get('/api/registros-mensuales/mis-registros');
      setRegistrosMensuales(response.data.data || []);
    } catch (error) {
      console.error('Error fetching registros:', error);
      // No mostrar error si el endpoint no existe, solo usar array vacío
    } finally {
      setLoadingRegistros(false);
    }
  }, []);

  // Calcular el total adeudado de un préstamo basado en registrosMensuales
  const calcularTotalAdeudado = (prestamo) => {
    // Capital pendiente = Monto total - Capital pagado en registros mensuales
    let capitalPagado = 0;
    let interesPagado = 0;
    
    registrosMensuales.forEach(reg => {
      capitalPagado += reg.capitalPrestamo || 0;
      interesPagado += reg.interesPrestamo || 0;
    });
    
    const capitalPendiente = Math.max(0, prestamo.monto - capitalPagado);
    const interesPendiente = Math.max(0, (prestamo.interesTotal || 0) - interesPagado);
    
    return capitalPendiente + interesPendiente;
  };

  // Calcular solo capital pendiente (sin interés) basado en registrosMensuales
  const calcularCapitalPendiente = (prestamo) => {
    let capitalPagado = 0;
    
    registrosMensuales.forEach(reg => {
      capitalPagado += reg.capitalPrestamo || 0;
    });
    
    return Math.max(0, prestamo.monto - capitalPagado);
  };

  // Función para formatear moneda
  const formatCurrency = (num) => {
    return parseFloat(num || 0).toLocaleString('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    const loadData = async () => {
      if (isSocio()) {
        setLoading(true);
        await Promise.all([fetchSocioData(), fetchPrestamos(), fetchRegistrosMensuales()]);
        setLoading(false);
      } else {
        setLoading(false);
        setLoadingPrestamos(false);
        setLoadingRegistros(false);
      }
    };
    loadData();
  }, [isSocio, fetchSocioData, fetchPrestamos, fetchRegistrosMensuales]);

  // Verificar qué préstamos están pagados después de cargar datos
  useEffect(() => {
    const nuevosCompletados = {};
    
    for (const prestamo of prestamos) {
      const detalles = calcularCuotaMensual(prestamo);
      const cronograma = generarCronogramaAmortizacion(prestamo, detalles, registrosMensuales);
      
      if (cronograma.length > 0) {
        const ultimoPeriodo = cronograma[cronograma.length - 1];
        const pagado = parseFloat(ultimoPeriodo.saldoRestante) === 0;
        
        if (pagado) {
          nuevosCompletados[prestamo._id] = true;
        }
      }
    }
    
    setPrestamosCompletados(nuevosCompletados);
  }, [prestamos, registrosMensuales]);

  // Cargar organizadores cuando cambia mes o año
  useEffect(() => {
    if (isSocio()) {
      fetchOrganizadoresReunion(mesReunion, añoReunion);
    }
  }, [mesReunion, añoReunion, isSocio, fetchOrganizadoresReunion]);

  if (!isSocio()) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Este dashboard es solo para socios.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  const socioInfo = socioData?.socioInfo || {};
  
  // Calcular aportaciones sumando todos los registros mensuales
  const aportaciones = registrosMensuales.reduce((acc, reg) => {
    return acc + (reg.aportes || 0);
  }, 0);
  
  // Calcular paseos sumando todos los registros mensuales
  const paseoTotal = registrosMensuales.reduce((acc, reg) => {
    return acc + (reg.paseo || 0);
  }, 0);

  // Calcular programa sumando todos los registros mensuales
  const programaTotal = registrosMensuales.reduce((acc, reg) => {
    return acc + (reg.programa || 0);
  }, 0);

  // Calcular multas sumando todos los registros mensuales
  const multasTotal = registrosMensuales.reduce((acc, reg) => {
    return acc + (reg.multas || 0);
  }, 0);

  // Función para verificar si un préstamo está completamente pagado
  const isPrestamoPagado = (prestamo) => {
    const detalles = calcularCuotaMensual(prestamo);
    const cronograma = generarCronogramaAmortizacion(prestamo, detalles, registrosMensuales);
    
    if (cronograma.length === 0) {
      return false;
    }
    
    const ultimoPeriodo = cronograma[cronograma.length - 1];
    return parseFloat(ultimoPeriodo.saldoRestante) === 0;
  };
  
  // Calcular el total adeudado usando el ÚLTIMO PERÍODO del cronograma
  // ✅ Obtiene el saldo actual real de cada préstamo
  const totalCapitalPendiente = prestamos
    .filter(p => {
      const esActivo = esPrestamoActivo(p.estado);
      const noPagado = !isPrestamoPagado(p);
      return esActivo && noPagado;
    })
    .reduce((acc, p) => {
      const detalles = calcularCuotaMensual(p);
      const cronograma = generarCronogramaAmortizacion(p, detalles, registrosMensuales);
      const periodoDeHoy = obtenerPeriodoDeHoy(cronograma);

      if (periodoDeHoy) {
        return acc + parseFloat(periodoDeHoy.saldoRestante || 0);
      }

      return acc;
    }, 0);

  const totalAdeudadoConInteres = prestamos
    .filter(p => {
      const esActivo = esPrestamoActivo(p.estado);
      const noPagado = !isPrestamoPagado(p);
      return esActivo && noPagado;
    })
    .reduce((acc, p) => {
      const detalles = calcularCuotaMensual(p);
      const cronograma = generarCronogramaAmortizacion(p, detalles, registrosMensuales);
      const periodoDeHoy = obtenerPeriodoDeHoy(cronograma);

      if (periodoDeHoy) {
        return acc + parseFloat(periodoDeHoy.saldoTotalConInteres || 0);
      }

      return acc;
    }, 0);

  const getEstadoBadge = (estado, isPagado = false) => {
    if (isPagado) {
      return 'bg-success';
    }
    
    switch (estado) {
      case 'aprobado': return 'bg-success';
      case 'pendiente': return 'bg-warning text-dark';
      case 'rechazado': return 'bg-danger';
      case 'en_revision': return 'bg-info';
      case 'desembolsado': return 'bg-primary';
      default: return 'bg-secondary';
    }
  };

  return (
    <Container fluid className="mt-4">
      {/* Encabezado de bienvenida */}
      <Row className="mb-4">
        <Col>
          <Card className="bg-primary text-white">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <h2 className="mb-1">¡Bienvenido, {user?.name}!</h2>
                  <p className="mb-0 opacity-75">
                    Socio #{socioInfo.numeroSocio} | Miembro desde {new Date(socioInfo.fechaIngreso).toLocaleDateString()}
                  </p>
                </Col>
                <Col md={4} className="text-end">
                  <i className="fas fa-user-circle fa-4x opacity-75"></i>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lo que debe hoy */}
      <MisDeudas />

      {/* Tarjetas de resumen financiero */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-piggy-bank fa-3x text-success"></i>
              </div>
              <h3 className="text-success mb-1">${aportaciones.toLocaleString()}</h3>
              <p className="text-muted mb-0">Aportaciones</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-receipt fa-3x text-info"></i>
              </div>
              <h3 className="text-info mb-1">${paseoTotal.toLocaleString()}</h3>
              <p className="text-muted mb-0">Paseo</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-calculator fa-3x text-warning"></i>
              </div>
              <h3 className="text-warning mb-1">${totalCapitalPendiente.toLocaleString()}</h3>
              <p className="text-muted mb-0">Adeudado (Sin Interés)</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-hand-holding-usd fa-3x text-danger"></i>
              </div>
              <h3 className="text-danger mb-1">${totalAdeudadoConInteres.toLocaleString()}</h3>
              <p className="text-muted mb-0">Adeudado (Con Interés)</p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-star fa-3x text-primary"></i>
              </div>
              <h3 className="text-primary mb-1">${programaTotal.toLocaleString()}</h3>
              <p className="text-muted mb-0">Programa</p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3">
                <i className="fas fa-gavel fa-3x text-danger"></i>
              </div>
              <h3 className="text-danger mb-1">${multasTotal.toLocaleString()}</h3>
              <p className="text-muted mb-0">Multas</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sección de Reuniones */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-map-marker-alt me-2"></i>
                Mi Lugar de Reunión
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <label className="fw-bold">Selecciona el Mes:</label>
                  <select 
                    className="form-select"
                    value={mesReunion}
                    onChange={(e) => setMesReunion(parseInt(e.target.value))}
                  >
                    <option value={1}>Enero</option>
                    <option value={2}>Febrero</option>
                    <option value={3}>Marzo</option>
                    <option value={4}>Abril</option>
                    <option value={5}>Mayo</option>
                    <option value={6}>Junio</option>
                    <option value={7}>Julio</option>
                    <option value={8}>Agosto</option>
                    <option value={9}>Septiembre</option>
                    <option value={10}>Octubre</option>
                    <option value={11}>Noviembre</option>
                    <option value={12}>Diciembre</option>
                  </select>
                </Col>
                <Col md={6}>
                  <label className="fw-bold">Año:</label>
                  <input 
                    type="number"
                    className="form-control"
                    value={añoReunion}
                    onChange={(e) => setAñoReunion(parseInt(e.target.value))}
                    min="2026"
                    max="2030"
                  />
                </Col>
              </Row>

              {socioData?.lugarReunion || socioData?.fecha ? (
                <div>
                  <div className="alert alert-info p-3">
                    <Row>
                      <Col md={4} className="text-center border-end">
                        <h6 className="text-muted mb-2">
                          <i className="fas fa-map-marker-alt me-2"></i>Lugar
                        </h6>
                        <h5 className="text-info mb-0">
                          {socioData.lugarReunion || 'Por definir'}
                        </h5>
                      </Col>
                      <Col md={4} className="text-center border-end">
                        <h6 className="text-muted mb-2">
                          <i className="fas fa-calendar me-2"></i>Fecha
                        </h6>
                        {socioData?.fecha ? (
                          <>
                            <h5 className="text-primary mb-0">
                              {new Date(socioData.fecha).toLocaleDateString('es-ES', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </h5>
                            <small className="text-muted">
                              ({new Date(socioData.fecha).toLocaleDateString('es-ES', { 
                                weekday: 'long' 
                              })})
                            </small>
                          </>
                        ) : (
                          <h5 className="text-muted mb-0">Por asignar</h5>
                        )}
                      </Col>
                      <Col md={4} className="text-center">
                        <h6 className="text-muted mb-2">
                          <i className="fas fa-clock me-2"></i>Hora
                        </h6>
                        <h5 className="text-success mb-0">
                          {socioData?.horaInicio || 'Por definir'}
                        </h5>
                      </Col>
                    </Row>
                  </div>
                  {socioData?.porDefinir && (
                    <Alert variant="warning" className="mt-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Hora por definir</strong> - La hora de la reunión aún no ha sido asignada
                    </Alert>
                  )}
                </div>
              ) : (
                <Alert variant="secondary">
                  <i className="fas fa-info-circle me-2"></i>
                  No hay lugar de reunión asignado aún. Contacta con la secretaría para obtener tu asignación.
                </Alert>
              )}

              {/* Quiénes organizan la reunión */}
              <hr className="my-4" />
              <h6 className="text-muted mb-3">
                <i className="fas fa-users me-2"></i>Quiénes organizan la reunión en {getNombreMes(mesReunion)} de {añoReunion}:
              </h6>
              
              {loadingOrganizadores ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : organizadoresReunion?.asignados?.length > 0 || organizadoresReunion?.familias?.length > 0 ? (
                <div className="mt-3">
                  {/* Mostrar socios individuales */}
                  {organizadoresReunion.asignados && organizadoresReunion.asignados.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-primary mb-2">
                        <i className="fas fa-user-circle me-2"></i>Socios Individuales:
                      </h6>
                      {organizadoresReunion.asignados.map((socio, idx) => (
                        <div key={idx} className="alert alert-light border-start border-primary ps-3 mb-2">
                          <div className="fw-bold">{socio.nombre}</div>
                          <small className="text-muted">{socio.email}</small>
                          {socio.phone && <small className="text-muted d-block">Tel: {socio.phone}</small>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Mostrar familias */}
                  {organizadoresReunion.familias && organizadoresReunion.familias.length > 0 && (
                    <div>
                      <h6 className="text-success mb-2">
                        <i className="fas fa-home me-2"></i>Familia Responsable:
                      </h6>
                      {organizadoresReunion.familias.map((familia, idx) => (
                        <div key={idx} className="alert alert-light border-start border-success ps-3">
                          <div className="fw-bold mb-2">{familia.nombre}</div>
                          {familia.lugar && <small className="d-block mb-1"><strong>Lugar:</strong> {familia.lugar}</small>}
                          {familia.hora && <small className="d-block mb-2"><strong>Hora:</strong> {familia.hora}</small>}
                          <div className="mt-2">
                            <small className="d-block text-muted mb-1"><strong>Miembros:</strong></small>
                            {familia.miembros.map((miembro, midx) => (
                              <div key={midx} className="ps-3 mb-1">
                                <small>
                                  <i className="fas fa-user me-1"></i>
                                  {miembro.nombre} 
                                  {miembro.rol === 'jefe' && <Badge bg="warning" className="ms-2">Jefe</Badge>}
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Alert variant="info" className="mt-3">
                  <i className="fas fa-info-circle me-2"></i>
                  No hay socios o familias asignados para organizar en {getNombreMes(mesReunion)} de {añoReunion} aún.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cronograma de Amortización */}
      {prestamos.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Cronograma de Amortización
                </h5>
              </Card.Header>
              <Card.Body>
                {prestamos.map((prestamo, idx) => {
                  if (prestamo.estado !== 'aprobada' && prestamo.estado !== 'desembolsado' && prestamo.estado !== 'aprobado') {
                    return null;
                  }
                  
                  const detalles = calcularCuotaMensual(prestamo);
                  const cronograma = generarCronogramaAmortizacion(prestamo, detalles, registrosMensuales);

                  return (
                    <div key={prestamo._id} className="mb-4">
                      <h6 className="text-muted mb-3">
                        {prestamo.numeroSolicitud} - Monto: {formatCurrency(prestamo.monto)}
                      </h6>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm" className="mb-0">
                          <thead className="table-primary">
                            <tr>
                              <th>Período</th>
                              <th>Mes - Año</th>
                              <th>Cuota Mensual</th>
                              <th>Interés</th>
                              <th>Estado</th>
                              <th>Capital Pagado</th>
                              <th>Interés Pagado</th>
                              <th>Saldo Restante</th>
                              <th>Saldo Total + Interés</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cronograma.map((fila, filIdx) => {
                              let rowClass = '';
                              if (fila.estado === 'Pagado') rowClass = 'table-success';
                              else if (fila.estado === 'Pagó Interés') rowClass = 'table-info';
                              else if (fila.estado === 'Impago') rowClass = 'table-danger';
                              else if (fila.estado === 'Pendiente') rowClass = 'table-warning';
                              
                              return (
                                <tr key={filIdx} className={rowClass}>
                                  <td className="text-center"><strong>{fila.periodo}</strong></td>
                                  <td>{fila.mes} {fila.año}</td>
                                  <td className="text-right">{formatCurrency(parseFloat(fila.cuotaMensual))}</td>
                                  <td className="text-right">{formatCurrency(parseFloat(fila.interes))}</td>
                                  <td className="text-center">
                                    <Badge bg={
                                      fila.estado === 'Pagado' ? 'success' :
                                      fila.estado === 'Pagó Interés' ? 'info' :
                                      fila.estado === 'Impago' ? 'danger' :
                                      fila.estado === 'Pendiente' ? 'warning' :
                                      'secondary'
                                    }>
                                      {fila.estado === 'Pagado' && '✓ Pagado'}
                                      {fila.estado === 'Pagó Interés' && '💰 Pagó Interés'}
                                      {fila.estado === 'Impago' && '✗ Impago'}
                                      {fila.estado === 'Pendiente' && '⏳ Pendiente'}
                                      {fila.estado === 'No vencido' && '⏹️ No vencido'}
                                    </Badge>
                                  </td>
                                  <td className="text-right">{formatCurrency(parseFloat(fila.capitalPagado))}</td>
                                  <td className="text-right">{formatCurrency(parseFloat(fila.interesPagado))}</td>
                                  <td className="text-right"><strong>{formatCurrency(parseFloat(fila.saldoRestante))}</strong></td>
                                  <td className="text-right"><strong className="text-danger">{formatCurrency(parseFloat(fila.saldoTotalConInteres))}</strong></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Mis Solicitudes de Préstamo */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-file-alt me-2"></i>
                Mis Solicitudes de Préstamo
              </h5>
            </Card.Header>
            <Card.Body>
              {loadingPrestamos ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> Cargando préstamos...
                </div>
              ) : prestamos.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th># Solicitud</th>
                      <th>Fecha</th>
                      <th>Monto</th>
                      <th>Plazo (meses)</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prestamos.map(p => {
                      const pagado = isPrestamoPagado(p);
                      return (
                        <tr key={p._id}>
                          <td>{p.numeroSolicitud}</td>
                          <td>{new Date(p.fechaSolicitud).toLocaleDateString()}</td>
                          <td>${p.monto.toLocaleString()}</td>
                          <td>{p.plazo}</td>
                          <td>
                            <span className={`badge ${getEstadoBadge(p.estado, pagado)}`}>
                              {pagado ? 'PAGO COMPLETO' : p.estado.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">No tienes solicitudes de préstamo registradas.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Información personal */}
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-user me-2"></i>
                Información Personal
              </h5>
            </Card.Header>
            <Card.Body>
              <Table borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold">Nombre:</td>
                    <td>{socioData?.name}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Cédula:</td>
                    <td>{socioData?.cedula}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Email:</td>
                    <td>{socioData?.email}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Teléfono:</td>
                    <td>{socioData?.phone || 'No registrado'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Dirección:</td>
                    <td>{socioData?.address || 'No registrada'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Estado:</td>
                    <td>
                      <span className={`badge ${socioData?.isActive ? 'bg-success' : 'bg-danger'}`}>
                        {socioData?.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Información de socio */}
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-id-card me-2"></i>
                Información de Socio
              </h5>
            </Card.Header>
            <Card.Body>
              <Table borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold">Número de Socio:</td>
                    <td>
                      <span className="badge bg-primary">{socioInfo.numeroSocio}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Fecha de Ingreso:</td>
                    <td>{new Date(socioInfo.fechaIngreso).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Estado de Socio:</td>
                    <td>
                      <span className={`badge ${
                        socioInfo.estado === 'activo' ? 'bg-success' :
                        socioInfo.estado === 'suspendido' ? 'bg-warning' : 'bg-danger'
                      }`}>
                        {socioInfo.estado?.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Beneficiario:</td>
                    <td>{socioInfo.beneficiario || 'No registrado'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Observaciones:</td>
                    <td>{socioInfo.observaciones || 'Ninguna'}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Resumen financiero detallado */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                Resumen Financiero Detallado
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="text-center p-3 border rounded mb-3">
                    <h6 className="text-warning">Adeudado Sin Interés</h6>
                    <h4 className="text-warning">${totalCapitalPendiente.toLocaleString()}</h4>
                    <small className="text-muted">Solo Capital Pendiente</small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="text-center p-3 border rounded mb-3">
                    <h6 className="text-danger">Adeudado Con Interés</h6>
                    <h4 className="text-danger">${totalAdeudadoConInteres.toLocaleString()}</h4>
                    <small className="text-muted">Capital + Interés Pendiente</small>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <div className="text-center p-3 border rounded">
                    <h6 className="text-success">Aportaciones</h6>
                    <h4 className="text-success">${aportaciones.toLocaleString()}</h4>
                    <small className="text-muted">Total Aportado</small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="text-center p-3 border rounded">
                    <h6 className="text-info">Paseo</h6>
                    <h4 className="text-info">${paseoTotal.toLocaleString()}</h4>
                    <small className="text-muted">Total de Paseos</small>
                  </div>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={6}>
                  <div className="text-center p-3 border rounded">
                    <h6 className="text-danger">Multas</h6>
                    <h4 className="text-danger">${multasTotal.toLocaleString()}</h4>
                    <small className="text-muted">Total de Multas</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Mensaje informativo */}
      <Row className="mt-4">
        <Col>
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Información importante:</strong> Los valores mostrados son actualizados por la administración. 
            Para consultas sobre movimientos específicos o actualizaciones, contacta con la cooperativa.
          </Alert>
        </Col>
      </Row>
    </Container>
  );
};

export default SocioDashboard;